import React from 'react'
import { useIntl } from 'react-intl'

import { SectionCurve } from '../../../components/SectionCurve'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { useRoutes } from '../../../routes'
import { SectionTestimonials } from '../../home/SectionTestimonials'
import { Section3CardsBenefits } from '../Section3CardsBenefits'
import { SectionGridBenefits } from '../SectionGridBenefits'
import { SectionHero } from '../SectionHero'
import { SectionQuote } from '../SectionQuote'
import { SectionSwitchedBenefits } from '../SectionSwitchedBenefits'
import { SectionTable } from '../SectionTable'

export default function CompareToGuruPage() {
  const routes = useRoutes()
  const intl = useIntl()

  const title = intl.formatMessage({
    defaultMessage: 'Slite - Best Guru alternative for team knowledge',
    id: 'compare.guru.page.seo.title',
  })

  const description = intl.formatMessage({
    defaultMessage: `Slite is the AI-powered knowledge base that enables fast-growing teams to effortlessly create, find and manage their company knowledge at scale. Try it for free today.`,
    id: 'compare.guru.page.seo.description',
  })

  return (
    <SlitePage
      canonicalUrl={routes.compare.detail({ slug: 'alternative-to-guru' })}
      background="moon-dust"
      title={title}
      description={description}
      ogImage="https://cdn.sanity.io/images/dnsgqii5/production/270b8aaab4df514de86f093ec0604bfa089dbef0-2100x1200.jpg"
    >
      <SectionHero
        competitorName="Guru"
        title={intl.formatMessage({
          defaultMessage:
            'Best Guru alternative for all types of company knowledge.',
          id: 'compare.guru.page.title',
        })}
        subtitle={intl.formatMessage({
          defaultMessage:
            'Slite is a much more collaborative team knowledge base flexible for all types of knowledge. Unlike Guru, which tends to be used only for static information among operations-heavy teams, Slite is designed to become a single source of truth that can be successfully leveraged by the whole company.',
          id: 'compare.guru.page.subtitle',
        })}
      />

      <SectionCurve from="moon-dust" to="shade-100" shape="top-large" />

      <Section3CardsBenefits
        title={intl.formatMessage(
          {
            defaultMessage: 'Why are teams choosing {br} Slite over Guru?',
            id: 'compare.guru.featureSection.title',
          },
          {
            br: <br className="display-none-small" />,
          }
        )}
        benefits={[
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/1b26c618e99a3605bd3f91e61fce4e6e64e673e7-1382x945.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Complete knowledge base illustration',
              id: 'compare.guru.feature1.image.alt',
            }),
            imageBackground: 'saturn-orange',
            title: intl.formatMessage({
              defaultMessage: 'All company knowledge, centralized.',
              id: 'compare.guru.feature1.title',
            }),
            description: intl.formatMessage({
              id: 'compare.guru.feature1.description',
              defaultMessage:
                'Guru is mostly known for building a fairly static wiki in limited cards format. Slite is flexible for all types of knowledge - from static handbooks, to dynamic product specs and living everyday docs.',
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/eb3db30de3a651a1f12def6d7d4e8c182552db92-1019x922.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Knowledge maintenance illustration',
              id: 'compare.guru.feature2.image.alt',
            }),
            imageBackground: 'pink-moon',
            title: intl.formatMessage({
              id: 'compare.guru.feature2.title',
              defaultMessage: 'Maintenance on autopilot.',
            }),
            description: intl.formatMessage({
              id: 'compare.guru.feature2.description',
              defaultMessage:
                "Guru offers knowledge management features but you still have to manage every doc one by one. With Slite's AI-suggested actions in the Knowledge Management Panel, you can effortlessly manage all your docs in bulk, at scale.",
            }),
          },
          {
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/cdab5cd1557180346c21c2d041c44751b81a14c9-800x622.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Universal adoption illustration',
              id: 'compare.guru.feature3.image.alt',
            }),
            imageBackground: 'primary',
            title: intl.formatMessage({
              id: 'compare.guru.feature3.title',
              defaultMessage: 'Well adopted company-wide.',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Guru is known for being mostly used by heavy operational teams, usually in support - characterized by a few contributors and many readers. Meanwhile Slite is well adopted across the whole company without excessive focus on any single team.',
              id: 'compare.guru.feature3.description',
            }),
          },
        ]}
      />

      <SectionGridBenefits
        mainColor="primary"
        title={intl.formatMessage({
          defaultMessage:
            'Your go-to place for all types of company knowledge.',
          id: 'compare.guru.benefitsSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage:
                'Can finally all teams document in one single place?',
              id: 'compare.guru.grid.benefit1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Slite from the very beginning has been built to suit every use case and meet the needs of all teams - from onboarding guides to all sorts of handbooks, from SOPs to daily living docs and meeting notes.',
              id: 'compare.guru.grid.benefit1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/b54f3501ff819202e3a22d30280aabe83f1644fe-1375x852.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'All teams documentation illustration',
              id: 'compare.guru.grid.benefit1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'How can I keep my docs organized?',
              id: 'compare.guru.grid.benefit2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Organize docs in flexible Collections. Filter, sort, and save views so your content is easy to consume.',
              id: 'compare.guru.grid.benefit2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/0c8931c0feb333d4d8b1e57e78467566decc2235-1374x1120.png',
            imageClassnames: '',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Collections organization illustration',
              id: 'compare.guru.grid.benefit2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'Are decisions also knowledge?',
              id: 'compare.guru.grid.benefit3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Get into the habit of documenting important questions and answers in one place. Use our dedicated space for structured decision-making, involve people that matter and set deadlines.',
              id: 'compare.guru.grid.benefit3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/3272fd655050553d144e9d7e11f00165f232c0b2-1375x1120.png',
            imageClassnames: 'flexbox flexbox-justify-end',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Decision-making illustration',
              id: 'compare.guru.grid.benefit3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'And how do I make my docs well-digestible?',
              id: 'compare.guru.grid.benefit4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Use sketches, videos, or other embed options to convey your ideas clearly. Add beautiful covers, timely hints, quotes, and other formatting so your docs are a joy to read. Use AI assistant to improve your doc as you write.',
              id: 'compare.guru.grid.benefit4.description',
            }),
            imageClassnames: 'padding-horizontal-24',
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/c9565fbacbd2bc9a0c5653198edc1622579a3ff7-1375x852.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Readable docs illustration',
              id: 'compare.guru.grid.benefit4.image.alt',
            }),
          },
        ]}
      />

      <SectionCurve from="shade-100" to="primary" shape="top-large" />
      <SectionQuote
        background="primary"
        quote={intl.formatMessage({
          defaultMessage: `"With 20 years in software development and support, I've worked with a number of wiki platforms. Slite is by far the easiest to enter information into and format it in a human readable way. This has led to widespread company utilization across departments, making it an invaluable business tool."`,
          id: 'compare.guru.quote.testimonial.text',
        })}
        authorImageUrl="https://cdn.sanity.io/images/dnsgqii5/production/d07eac3684aee37617b650a5b2e9aadc60938934-400x400.webp"
        authorTitle="Rosie Marshall"
        authorDescription="Product manager, Clear Impact"
      />
      <SectionCurve
        from="moon-dust"
        to="primary"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionSwitchedBenefits
        title={intl.formatMessage({
          defaultMessage: 'Sounds great, but I have a few questions...',
          id: 'compare.guru.faqSection.title',
        })}
        data={[
          {
            title: intl.formatMessage({
              defaultMessage:
                'Guru is known for being focused on knowledge management, so how is Slite different?',
              id: 'compare.guru.faq1.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'While Guru emphasizes knowledge management, many features are still rather manual. With Slite you can benefit from Knowledge Management Panel where you can not only slice and dice all your docs in one place but also use quick AI-suggested actions to keep your knowledge fresh with a single click.',
              id: 'compare.guru.faq1.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/1b5d8ef4ae99f70ba1b765de5bbd5322d5b1007d-698x361.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Knowledge Management Panel illustration',
              id: 'compare.guru.faq1.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                "It's hard to navigate through docs with Guru. How does Slite's navigation system work?",
              id: 'compare.guru.faq2.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'Slite offers a seamless tree structure that is easy to work with. Special channel settings also allow to highlight inactive and archived docs at a glance.',
              id: 'compare.guru.faq2.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/da165e702eb11265d89d15fdebc056b93eb8e814-698x361.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Tree navigation illustration',
              id: 'compare.guru.faq2.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'Will my team still need to use several knowledge management tools?',
              id: 'compare.guru.faq3.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "No more separate tools, no more siloed teams. We often hear that teams tend to use Guru for support teams and other, more intuitive knowledge base for the rest of the team. With Slite, there's no need for two tools as it's well-suited to meet the needs of all teams.",
              id: 'compare.guru.faq3.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/8299f8f1190b86ad71dfb5a012cf5250852c34e5-698x361.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Unified knowledge base illustration',
              id: 'compare.guru.faq3.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage: 'Does Slite offer desktop and mobile apps?',
              id: 'compare.guru.faq4.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                "Absolutely! While Guru claims to have a mobile app, let's be real—it's just a mobile web experience. With Slite, you get quick and easy access to your team's work and all the info you need, whether you're on desktop or using your preferred mobile app.",
              id: 'compare.guru.faq4.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/d993e1c0284934b0deea0f44accc916680985eb0-698x361.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Desktop and mobile apps illustration',
              id: 'compare.guru.faq4.image.alt',
            }),
          },
          {
            title: intl.formatMessage({
              defaultMessage:
                'And what about the pricing - which one is cheaper?',
              id: 'compare.guru.faq5.title',
            }),
            description: intl.formatMessage({
              defaultMessage:
                'No need to pay more for less flexibility. With Slite you pay 20% less and get even more valuable capabilities than what Guru offers.',
              id: 'compare.guru.faq5.description',
            }),
            imageUrl:
              'https://cdn.sanity.io/images/dnsgqii5/production/002a531d88040abb4410254de4f37a8e86897ea9-698x361.png',
            imageAlt: intl.formatMessage({
              defaultMessage: 'Pricing comparison illustration',
              id: 'compare.guru.faq5.image.alt',
            }),
          },
        ]}
      />

      <SectionCurve from="moon-dust" to="shade-00" shape="top-large" />
      <SectionTable
        competitorName="Guru"
        title={intl.formatMessage({
          defaultMessage: 'Not flexible enough for your whole company.',
          id: 'compare.guru.comparisonTable.title',
        })}
        description={intl.formatMessage({
          defaultMessage:
            'Guru has verification and search, but no MCP, no AI on base plans, and limited flexibility beyond support teams. Slite covers all of that with a simpler, more modern approach.',
          id: 'compare.guru.comparisonTable.description.v2',
        })}
        checkedFeatures={[
          'enterprise-search',
          'document-verification',
          'knowledge-management-panel',
        ]}
      />
      <SectionCurve
        from="moon-dust"
        to="shade-00"
        shape="top-large"
        style={{ transform: 'translateY(-1px) rotate(180deg)' }}
      />

      <SectionTestimonials withCaseStudies={false} />
      <SectionLastCTA background="moon-dust" />
    </SlitePage>
  )
}
